import React, { useCallback, useContext, useEffect, useState } from "react";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import styles from "./AssemblyVoting.module.css";
import { QuorumType } from "src/stores/assemblies/VotingStore";
import { runInAction } from "mobx";
import { getTime } from "src/utils/Date";
import { Voting } from "src/components/voting/Voting";

const AssemblyVotingTitle = observer(function AssemblyVotingTitle() {
    const { votingStore } = useContext(RootStoreContext);
    return <div className={styles.VotingTitle}>{votingStore.selectedVoting.question}</div>;
});

const AssemblyVotingResultText = observer(function AssemblyVotingResultText() {
    const { t } = useTranslation();
    const { votingStore } = useContext(RootStoreContext);

    if (!votingStore.displayedVotingResult || !votingStore.selectedVoting?.votingagendaitemid) {
        return null;
    }

    const votingResultText = !votingStore.isQuoratePerVoting
        ? t("screens.assemblies.vote.not_quorate")
        : !votingStore.displayedVotingResult.resultText
          ? t("screens.assemblies.vote.voting_not_accepted")
          : `[ ${votingStore.displayedVotingResult.resultText} ] ${
                votingStore.displayedVotingResult.votingaccepted
                    ? t("screens.assemblies.vote.voting_accepted")
                    : t("screens.assemblies.vote.voting_not_accepted")
            }`;

    if (votingStore.hasNoVotingOptions && votingStore.isQuoratePerVoting) {
        return null;
    }

    const renderContent = () => {
        return (
            <div className={!votingStore.isQuoratePerVoting ? styles.ResultHeadernotQuorate : styles.ResultHeader}>
                <div
                    className={
                        !votingStore.displayedVotingResult.votingaccepted || !votingStore.isQuoratePerVoting
                            ? styles.ResultHeaderIconWrapperTie
                            : styles.ResultHeaderIconWrapper
                    }
                    style={{ backgroundColor: votingStore.votingColorResult }}
                >
                    {!votingStore.displayedVotingResult.votingaccepted || !votingStore.isQuoratePerVoting ? (
                        <UI.Icon icon={UI.SVGIcon.Close} size={16} color="white" />
                    ) : (
                        <UI.Icon icon={UI.SVGIcon.Check} size={16} color="white" />
                    )}
                </div>
                <div className={styles.ResultHeaderContent}>
                    <div
                        className={
                            !votingStore.displayedVotingResult.votingaccepted || !votingStore.isQuoratePerVoting
                                ? styles.ResultHeaderTextTie
                                : styles.ResultHeaderText
                        }
                        style={{ color: votingStore.votingColorResult }}
                    >
                        {t("screens.assemblies.vote.voting_result")}: {votingResultText}
                    </div>
                    {votingStore.votingBasis.quorum === QuorumType.UNANIMITY ? (
                        <div className={styles.ResultHeaderSubtitle}>
                            {t("screens.assemblies.vote.all_tenant_required")}
                        </div>
                    ) : votingStore.votingBasis.quorum === QuorumType.QUALIFIED_MAJORITY ? (
                        [
                            { accepted: votingStore.displayedVotingResult.key1accepted, mls: votingStore.key1Mls },
                            { accepted: votingStore.displayedVotingResult.key2accepted, mls: votingStore.key2Mls }
                        ].map(({ accepted, mls }, i) => (
                            <div
                                key={i}
                                className={styles.ResultHeaderSubtitle}
                                dangerouslySetInnerHTML={{
                                    __html: votingStore.getQualifiedMajorityHtml(accepted, mls)
                                }}
                            />
                        ))
                    ) : (
                        <div
                            className={styles.ResultHeaderSubtitle}
                            dangerouslySetInnerHTML={{
                                __html: votingStore.getQualifiedMajorityHtml(
                                    votingStore.displayedVotingResult.key1accepted,
                                    votingStore.key1Mls
                                )
                            }}
                        />
                    )}
                </div>
            </div>
        );
    };

    if (votingStore.isQuoratePerVoting) {
        return renderContent();
    }

    return (
        <UI.OverlayTrigger
            flip={true}
            overlay={
                <UI.Tooltip id={"assemblyVotingQuorumInfo-tooltip"} className={styles.Tooltip}>
                    {t("screens.realestate.assembly.assembly_quorum_tooltip")}
                </UI.Tooltip>
            }
        >
            {renderContent()}
        </UI.OverlayTrigger>
    );
});

const AssemblyVotingOptionTotal = observer(function AssemblyVotingOptionTotal() {
    const { votingStore } = useContext(RootStoreContext);

    const { votingOptions, displayedVotingResult, votingColors } = votingStore;

    if (!displayedVotingResult || displayedVotingResult.key1details === undefined) {
        return null;
    }

    const hasKey2 = !!displayedVotingResult?.key2details;

    if (displayedVotingResult.key1details === null) {
        return null;
    }

    return (
        <div className={styles.ResultContainer}>
            {votingOptions.map((option, index) => {
                const color = votingColors[index];

                const key1Val =
                    displayedVotingResult?.key1details?.find((d) => d.votingoptionid === option.votingoptionid)
                        ?.votingoptiondresult ?? 0;

                const key2Val =
                    displayedVotingResult?.key2details?.find((d) => d.votingoptionid === option.votingoptionid)
                        ?.votingoptiondresult ?? 0;

                return (
                    <div key={option.votingoptionid} className={styles.ResultItem}>
                        <div className={styles.ResultNumber} style={{ color }}>
                            {key1Val}
                        </div>

                        {hasKey2 && (
                            <div className={styles.ResultSubNumber} style={{ color }}>
                                {key2Val}
                            </div>
                        )}

                        <div className={styles.ResultLabel} style={{ color }}>
                            {option.text}
                        </div>
                    </div>
                );
            })}
        </div>
    );
});

const AssemblyVotingPieChart = observer(function AssemblyVotingPieChart() {
    const { votingStore } = useContext(RootStoreContext);
    const isSimpleMajorityOrUnamity =
        votingStore.votingBasis.quorum === QuorumType.SIMPLE_MAJORITY ||
        votingStore.votingBasis.quorum === QuorumType.UNANIMITY;
    const isQualifiedMajority = votingStore.votingBasis.quorum === QuorumType.QUALIFIED_MAJORITY;

    if (isSimpleMajorityOrUnamity) {
        return (
            <>
                {votingStore.pieChartList.some((item) => item.number > 0) && (
                    <div className={styles.VotingInforAndPieChart}>
                        <div className={styles.PieChart}>
                            <UI.PieChart pieChartList={votingStore.pieChartList} />
                        </div>
                    </div>
                )}
            </>
        );
    } else if (isQualifiedMajority) {
        return (
            <>
                {votingStore.pieChartList.some((item) => item.number > 0) && (
                    <div className={styles.VotingInforAndPieChart}>
                        <div className={styles.PieChart}>
                            <UI.DoublePieChart
                                innerList={votingStore.pieChartList}
                                outerList={votingStore.pieChartOuterList}
                            />
                        </div>
                    </div>
                )}
            </>
        );
    }

    return null;
});

const AssemblyVotingStatsRow = observer(function AssemblyVotingStatsRow() {
    const { t } = useTranslation();
    const { votingStore } = useContext(RootStoreContext);

    const majorityValue =
        typeof votingStore.votingBasis.majority === "number" && votingStore.votingBasis.majority > 0
            ? `${votingStore.votingBasis.majority}%`
            : "-";

    return (
        <div className={styles.ResultStats}>
            <div className={styles.StatItem}>
                <div className={styles.StatTitle}>{votingStore.quorumMls}</div>
                <div className={styles.StatLabel}>{t("screens.assemblies.vote.quorum")}</div>
            </div>
            <div className={styles.StatItem}>
                <div className={styles.StatTitle}>
                    {votingStore.key1Mls}
                    {votingStore.key2Mls.length > 0 && ` / ${votingStore.key2Mls}`}
                </div>
                <div className={styles.StatLabel}>{t("screens.assemblies.vote.voting_method")}</div>
            </div>
            <div className={styles.StatItem}>
                <div className={styles.StatTitle}>{majorityValue}</div>
                <div className={styles.StatLabel}>{t("screens.assemblies.vote.majority")}</div>
                <div className={styles.StatLabel}>
                    {`(${
                        votingStore.votingBasis.entirety
                            ? t("screens.assemblies.vote.entirety")
                            : t("screens.assemblies.vote.present")
                    })`}
                </div>
            </div>
            <div className={styles.StatItem}>
                <div className={styles.StatTitle}>
                    {votingStore.isQuoratePerVoting ? t("labels.yes") : t("labels.no")}
                </div>
                <div className={styles.StatLabel}>{t("screens.assemblies.vote.quorate")}</div>
            </div>
        </div>
    );
});

const AssemblyVotingVoting = observer(function AssemblyVotingVoting({ mountKey }: { mountKey: number }) {
    const { votingStore, agendaitemStore } = useContext(RootStoreContext);
    const { t } = useTranslation();
    const { selectedVoting, votingOptions, votingColors, setVotingComponentResponse } = votingStore;

    const handleSelectionChange = useCallback(
        (data: { id: string; selectedColumn: number | null }[]) => {
            setVotingComponentResponse(data);
        },
        [setVotingComponentResponse]
    );

    const handleActiveChange = useCallback(
        (tenantId: string, nextActive: boolean) => {
            runInAction(() => {
                votingStore.setTenantActive(tenantId, nextActive);

                votingStore.setTenantListForQuorumCheck(tenantId, nextActive ? 0 : 5);

                votingStore.calcVotingAgendaitemQuorumState();
            });

            const tenant = votingStore.votingTenants.find((t) => t.tenantid === tenantId);
            const name = tenant?.name ?? "";

            const action = nextActive
                ? t("screens.realestate.assembly.joined_assembly")
                : t("screens.realestate.assembly.left_assembly");

            const time = getTime(new Date().toISOString());
            const logLine = `${name} ${action} um ${time} Uhr`;
            agendaitemStore.appendToProtocolText?.(`${logLine}`);
        },
        [votingStore, agendaitemStore, t]
    );

    const { key1, key2 } = votingStore.votingBasis;
    const key1Type = votingStore.keys.find((k) => k.keyid === key1)?.type ?? 0;
    const key2Type = key2 ? (votingStore.keys.find((k) => k.keyid === key2)?.type ?? 0) : 0;

    // When both keys are set and one of them is Objektstimmen (type 2) or Kopfstimmen (type 3),
    // show two label columns side by side: "WQ" (unitvalue) and "Stimmen"/"Köpfe".
    const hasBothKeys = !!key1 && !!key2;
    const hasObjektstimmen = key1Type === 2 || key2Type === 2;
    const hasKopfstimmen = key1Type === 3 || key2Type === 3;
    const showBothColumns = hasBothKeys && (hasObjektstimmen || hasKopfstimmen);

    // single-column mode: the visible column follows key1's type
    const isObjektstimmen = key1Type === 2;
    const isKopfstimmen = key1Type === 3;
    const firstColHeader = isObjektstimmen || isKopfstimmen ? "Stimmen" : "WQ";

    const wqLabels = votingStore.tenantRows.map((t) => t.unitvalue.toString());
    const stimmenLabels = votingStore.tenantRows.map((t) => t.unitcount.toString());
    const kopfLabels = votingStore.tenantRows.map(() => "1");
    const secondRowLabels = isObjektstimmen ? stimmenLabels : isKopfstimmen ? kopfLabels : wqLabels;

    return (
        <Voting
            key={`${selectedVoting.votingid}-${mountKey}`}
            numberOfColumn={votingOptions.length}
            headers={
                showBothColumns
                    ? ["WQ", "Stimmen", ...votingOptions.map((o) => o.text)]
                    : [firstColHeader, ...votingOptions.map((o) => o.text)]
            }
            withMainRow
            rows={votingStore.tenantRows}
            secondRowLabels={showBothColumns ? wqLabels : secondRowLabels}
            thirdRowLabels={showBothColumns ? (hasObjektstimmen ? stimmenLabels : kopfLabels) : undefined}
            buttonColors={votingColors}
            onSelectionChange={handleSelectionChange}
            withSearchInput
            onActiveChange={handleActiveChange}
            zeroUnitField={isObjektstimmen || isKopfstimmen ? "unitcount" : "unitvalue"}
        />
    );
});

const AssemblyVotingSaveButton = observer(function AssemblyVotingSaveButton() {
    const { t } = useTranslation();
    const { votingStore } = useContext(RootStoreContext);
    const { saveVotingResult } = votingStore;

    const handleSave = useCallback(() => {
        saveVotingResult();
    }, [saveVotingResult]);

    return (
        <div className={styles.SaveButtonWrapper}>
            <UI.Button
                onClick={handleSave}
                className={styles.SaveButton}
                label={t("labels.save")}
                disabled={!votingStore.hasVotingResultChanged}
            />
        </div>
    );
});

export const AssemblyVoting = observer(function AssemblyVotingBase() {
    const { votingStore } = useContext(RootStoreContext);
    const [isLoaded, setIsLoaded] = useState(false);
    const [votingMountKey, setVotingMountKey] = useState(0);

    useEffect(() => {
        const { votingid, votingagendaitemid } = votingStore.selectedVoting;

        if (!votingid || !votingagendaitemid) {
            setIsLoaded(false);
            return;
        }

        setIsLoaded(false);
        votingStore.setVotingComponentResponse([]);
        votingStore.tenantListForQuorumCheck = [];

        let isStale = false;

        const loadAll = async () => {
            await votingStore.loadVotingTenantsAndOptions();
            if (isStale) return;

            await votingStore.loadVotingInformationBasis();
            if (isStale) return;

            await votingStore.loadVotingDetails(votingid, votingagendaitemid);
            if (isStale) return;

            await votingStore.getVotingAgendaitemQuorumState();
            if (isStale) return;

            if (votingStore.isQuoratePerVoting) {
                await votingStore.getVotingResult();
                if (isStale) return;
            }

            setIsLoaded(true);
        };

        void loadAll();

        return () => {
            isStale = true;
        };
    }, [
        votingStore.selectedVoting.votingid,
        votingStore.selectedVoting.votingagendaitemid,
        votingStore.selectedVotingRefreshKey,
        votingStore
    ]);

    useEffect(() => {
        const { votingTenants, votingOptions, votingResults } = votingStore;

        if (
            votingStore.selectedVoting.votingid &&
            votingTenants.length > 0 &&
            votingOptions.length > 0 &&
            votingResults.length > 0
        ) {
            const initialResponse = votingTenants.map((tenant) => {
                const result = votingResults.find((r) => r.tenantid === tenant.tenantid);
                if (!result?.votingoptionid) {
                    return { id: tenant.tenantid, selectedColumn: null };
                }
                const optIndex = votingOptions.findIndex((o) => o.votingoptionid === result.votingoptionid);
                return { id: tenant.tenantid, selectedColumn: optIndex >= 0 ? optIndex + 1 : null };
            });

            votingStore.setVotingComponentResponse(initialResponse);
            setVotingMountKey((k) => k + 1);
        }
    }, [
        votingStore,
        votingStore.selectedVoting.votingid,
        votingStore.votingTenants,
        votingStore.votingOptions,
        votingStore.votingResults,
        votingStore.setVotingComponentResponse
    ]);

    if (
        !isLoaded ||
        !votingStore.selectedVoting.votingid ||
        votingStore.votingTenants.length === 0 ||
        votingStore.votingOptions.length === 0 ||
        votingStore.votingResults.length === 0
    ) {
        return null;
    }

    return (
        <UI.Card>
            <AssemblyVotingTitle />
            <div className={styles.ResultInformations}>
                <AssemblyVotingResultText />
                <AssemblyVotingOptionTotal />
                <AssemblyVotingPieChart />
                <AssemblyVotingStatsRow />
            </div>
            <AssemblyVotingVoting mountKey={votingMountKey} />
            <AssemblyVotingSaveButton />
        </UI.Card>
    );
});
